import React from "react";
import { LegalLayout } from "../../components/LegalLayout";
import { LEGAL } from "../../lib/legal";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" testid="legal-terms-page">
      <p>
        These Terms of Service (<strong>"Terms"</strong>) govern your access to and use of {LEGAL.companyName} (the
        <strong> "Service"</strong>), an algorithmic trading simulation and signals platform. By creating an account or
        using the Service, you agree to these Terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be (a) at least 18 years old, (b) a resident of a jurisdiction where use of the Service is lawful, and
        (c) not subject to U.S. or international sanctions or prohibited from financial services. You represent that all
        information you provide is accurate and current.
      </p>

      <h2>2. Nature of the Service</h2>
      <p>
        The Service is an <strong>educational and simulation tool</strong>. It allows you to construct, test, and observe
        algorithmic trading strategies using paper-traded (simulated) capital and, optionally, to route orders to
        third-party exchanges using API keys you supply. The Service is not a broker, dealer, exchange, investment
        adviser, money transmitter, or fiduciary. We do not hold custody of any funds or assets.
      </p>

      <h2>3. No Investment Advice</h2>
      <p>
        Information delivered through the Service — including but not limited to "Alpha Signals", strategy templates,
        AI-generated rationales, dashboards, charts, and any aggregated statistics — is provided for{" "}
        <strong>informational and educational purposes only</strong>. It is not a recommendation, solicitation,
        endorsement, or offer to buy or sell any security, cryptocurrency, derivative, or other financial instrument.
        You are solely responsible for your trading decisions.
      </p>

      <h2>4. Account</h2>
      <p>
        You are responsible for safeguarding your credentials, including any third-party exchange API keys you store on
        the Service. You must promptly revoke any compromised API key at the originating exchange. We will never ask you
        for an exchange withdrawal-enabled key; do not provide one. You are responsible for all activity that occurs
        under your account.
      </p>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to, and not to permit any third party to:</p>
      <ul>
        <li>Use the Service for any unlawful, fraudulent, or manipulative trading purpose;</li>
        <li>Engage in market manipulation, wash trading, layering, spoofing, or any activity that violates applicable securities or commodities laws;</li>
        <li>Reverse engineer, scrape, or attempt to extract source code of the Service;</li>
        <li>Resell or sublicense the Service or its outputs without our written consent;</li>
        <li>Use the Service if you are prohibited from doing so under the laws of your jurisdiction.</li>
      </ul>

      <h2>6. Paid Features &amp; Refunds</h2>
      <p>
        Paid features (e.g., "Pro" tier access passes) are billed via Stripe. Charges are presented in U.S. dollars and
        clearly disclosed before purchase. Because access begins immediately on purchase, paid passes are{" "}
        <strong>non-refundable</strong> except where required by applicable law. If you believe a charge was made in
        error, contact <a href={`mailto:${LEGAL.legalEmail}`}>{LEGAL.legalEmail}</a> within 7 days of the transaction.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The Service integrates with third-party providers such as cryptocurrency exchanges, market data feeds, payment
        processors, and AI providers. We are not responsible for the availability, security, accuracy, or actions of
        these third parties. Their respective terms and policies apply to your use of their services.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        The Service, its software, design, and content (excluding your user-generated content and your exchange
        credentials) are owned by {LEGAL.companyName} and protected by U.S. and international copyright, trademark, and
        other laws. You receive a limited, revocable, non-exclusive, non-transferable license to use the Service for
        personal, non-commercial purposes.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
        INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
        ACCURACY, AVAILABILITY, OR PROFITABILITY. WE DO NOT GUARANTEE THAT ANY STRATEGY OR SIGNAL WILL BE PROFITABLE.
        PAST PERFORMANCE — REAL OR SIMULATED — IS NOT INDICATIVE OF FUTURE RESULTS.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {LEGAL.companyName.toUpperCase()} AND ITS OFFICERS, DIRECTORS,
        EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR TRADING LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE
        OF THE SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF
        (A) US$100 OR (B) THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless {LEGAL.companyName} from any claim, demand, loss, or damage
        (including reasonable attorneys' fees) arising out of or related to (a) your use of the Service, (b) your
        violation of these Terms, (c) your trading activity, or (d) your violation of any law or third-party rights.
      </p>

      <h2>12. Termination</h2>
      <p>
        We may suspend or terminate your access at any time for any reason, including violation of these Terms. You may
        terminate your account at any time from the Settings page; deletion is irreversible.
      </p>

      <h2>13. Governing Law &amp; Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the State of {LEGAL.governingState}, USA, without regard to conflict-of-law
        rules. Any dispute, claim, or controversy arising out of or relating to these Terms or the Service will be
        resolved by binding arbitration administered by JAMS in accordance with its Streamlined Arbitration Rules. The
        arbitration shall take place in {LEGAL.governingState}, in English, before a single arbitrator. You waive any
        right to a jury trial and any right to participate in a class action or class arbitration to the maximum extent
        permitted by law.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be communicated by email or in-app notice and
        will be effective on the date stated. Continued use of the Service after the effective date constitutes
        acceptance of the revised Terms.
      </p>

      <h2>15. Contact</h2>
      <p>Questions? Email <a href={`mailto:${LEGAL.legalEmail}`}>{LEGAL.legalEmail}</a>.</p>
    </LegalLayout>
  );
}
